import { useEffect, useRef } from 'react'
import { nextPollDelay } from '../../utils/orderConfirmation'

/**
 * One chain's work, handed to `start`.
 *
 * Per run rather than per hook, because a chain's values — the request it is
 * reading, the budget it was authored with, the promise its owner is waiting
 * on — are only known in the event handler that starts it. Handed to the hook
 * instead, they would be the previous render's.
 */
export interface PollRun<T> {
  /**
   * One request. Resolves to a verdict when the chain is done, or to
   * `undefined` to keep polling.
   *
   * Must not settle anything itself: the chain checks that it is still the
   * current one *after* this resolves and only then calls `onSettle`, which is
   * what stops a tick that was in flight across a `stop()` or a cut-off from
   * acting. It should catch its own failures, since it is the one that can
   * report them; a throw is read as "keep polling" and is otherwise silent.
   */
  tick: () => Promise<T | undefined>
  /** The verdict of the tick that ended the chain. */
  onSettle: (verdict: T) => void
  /**
   * The deadline passed with no verdict: either a tick came back empty-handed
   * at or past it, or the cut-off fired with a tick still awaiting its
   * response. Called at most once per chain.
   */
  onDeadline: () => void
  /** Delay between ticks after the first, in ms. Must be positive. */
  pollingTime: number
  /**
   * Wall-clock budget from `start`, in ms. A look issued at or past it (the
   * closing look, or a first look later than a short budget) gets
   * `pollingTime` more before the cut-off; see `usePollChain`.
   */
  timeout: number
}

export interface PollChainOptions {
  /** Delay before the chain's first tick, in ms. */
  firstLookMs: number
  /**
   * Whether unmounting the owner stops the chain.
   *
   * Both modes exist in this repo and are deliberate, so neither is the
   * default. `useGetOrderDetail` stops its read on unmount; a discarded late
   * answer costs nothing, since the screen can ask again.
   * `useOrderConfirmation` must not (COM-818): its outcome is owed to the
   * shopper and the store outlives the screen.
   */
  stopOnUnmount: boolean
}

export interface PollChain<T> {
  /** Starts a chain, superseding any chain this instance was running. */
  start: (run: PollRun<T>) => void
  /**
   * Stops the chain: cancels the scheduled tick and the cut-off, and stops a
   * tick already awaiting its response from settling or scheduling a
   * successor.
   */
  stop: () => void
}

/**
 * A single-timer poll loop with a superseded-request guard and a wall-clock
 * deadline, generalised from `useOrderConfirmation`'s poller (COM-1295).
 *
 * New code only for now: the two order pollers still carry their own copies,
 * and moving them onto this is M2's.
 *
 * What it preserves from those pollers, and why each matters:
 *
 * - **The budget check runs after the request** (COM-871). A tick that lands
 *   at or past the deadline is the chain's *last look*, not a request thrown
 *   away, and its verdict is acted on. `nextPollDelay` shortens the final
 *   delay so that look lands on the deadline rather than past it.
 * - **But a request cannot hold the chain open forever** (COM-1295 review).
 *   Apollo has no request timeout, so a check that only runs when a tick
 *   *returns* never runs for a request that hangs, and an owner's loader
 *   stays up indefinitely. A second timer, the **cut-off**, fires on its own:
 *   - armed by `start` at `timeout`. If a tick issued before the deadline is
 *     still awaiting its response then, the chain is stopped (token bumped,
 *     so the late answer is dropped) and `onDeadline` runs. A tick that
 *     returns before the deadline is acted on as before. If instead a look is
 *     scheduled and not yet issued (the closing look on the deadline, or a
 *     first look later than a short budget), the cut-off does nothing and
 *     leaves that look to run.
 *   - re-armed by `pollTick` at `pollingTime` when it issues a look at or past
 *     the deadline. That look has, by definition, no budget left; giving it
 *     none would make COM-871's last look dead code in production, since no
 *     network answer arrives in 0 ms. So the worst case an owner waits is
 *     `max(timeout, firstLookMs) + pollingTime`, never unbounded.
 *   Two arming sites, so each has its own kill case.
 * - **A token, not just a timer.** Clearing the timer cancels a tick that has
 *   not fired; it does nothing about one that has fired and is awaiting its
 *   response. Only the token stops that one, which is why `stop()` bumps it.
 *   `useOrderConfirmation`'s own `stopPolling` clears the timer alone, and
 *   relies on every caller bumping `requestIdRef` beside it.
 * - **One timer per instance.** `start` stops the previous chain first, so an
 *   instance can never run two.
 *
 * State lives in refs, so it is per instance: one instance cannot stop
 * another's chain.
 */
export const usePollChain = <T>({
  firstLookMs,
  stopOnUnmount,
}: PollChainOptions): PollChain<T> => {
  /** Bumped by every `start` and `stop`; a chain holds the value it began with. */
  const tokenRef = useRef(0)
  /** The one scheduled tick. */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** The cut-off for a tick in flight; see the docstring above. */
  const cutOffRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** When the current chain started, against which `timeout` is measured. */
  const startedAtRef = useRef(0)
  /** Read at unmount, so the mode is the latest render's. */
  const stopOnUnmountRef = useRef(stopOnUnmount)
  stopOnUnmountRef.current = stopOnUnmount

  const isSuperseded = (token: number) => tokenRef.current !== token

  const clearCutOff = () => {
    if (cutOffRef.current) {
      clearTimeout(cutOffRef.current)
      cutOffRef.current = null
    }
  }

  const stop = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    clearCutOff()
    tokenRef.current += 1
  }

  /*
   * The cut-off's one `setTimeout`, with two callers: `start` (at the
   * deadline) and `pollTick` (a look issued at or past it). Each needs its own
   * kill case, as `schedulePoll`'s do.
   */
  const armCutOff = (run: PollRun<T>, token: number, delay: number) => {
    clearCutOff()
    cutOffRef.current = setTimeout(() => {
      cutOffRef.current = null
      // Defensive: `stop` clears this timer before bumping the token.
      if (isSuperseded(token)) {
        return
      }
      // A look is scheduled and not yet issued: it is the last look, and it
      // re-arms the cut-off itself when it goes out.
      const isLookScheduled = timerRef.current !== null
      if (isLookScheduled) {
        return
      }
      stop()
      run.onDeadline()
    }, delay)
  }

  /*
   * The one `setTimeout`, with two callers: `start` (the first look) and
   * `pollTick`'s own tail (the cadence). Each needs its own kill case — a
   * single seam moves the per-site obligation to its callers rather than
   * discharging it.
   */
  const schedulePoll = (run: PollRun<T>, token: number, delay: number) => {
    timerRef.current = setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      pollTick(run, token)
    }, delay)
  }

  const pollTick = async (run: PollRun<T>, token: number): Promise<void> => {
    // This tick's timer has fired, so the ref no longer names anything
    // cancellable.
    timerRef.current = null
    // Defensive: `start` and `stop` both clear the timer before bumping the
    // token, so a superseded tick's timer never fires. The live guard is the
    // one after the `await` below.
    if (isSuperseded(token)) {
      return
    }

    const elapsedAtIssue = Date.now() - startedAtRef.current
    const isLastLook = elapsedAtIssue >= run.timeout
    if (isLastLook) {
      armCutOff(run, token, run.pollingTime)
    }

    let verdict: T | undefined
    try {
      verdict = await run.tick()
    } catch {
      // Defensive: `tick` is required to catch its own failures. A throw that
      // escaped would otherwise end the chain with no verdict and no deadline,
      // leaving its owner waiting forever.
      verdict = undefined
    }
    if (isSuperseded(token)) {
      return
    }

    if (verdict !== undefined) {
      // Cleared before the callback, which may `start` a new chain.
      clearCutOff()
      run.onSettle(verdict)
      return
    }

    // One sample, read by the deadline test and by the delay below, so the
    // delay is strictly positive with no clamp (see `nextPollDelay`).
    const elapsed = Date.now() - startedAtRef.current
    const isPastDeadline = elapsed >= run.timeout
    if (isPastDeadline) {
      clearCutOff()
      run.onDeadline()
      return
    }

    schedulePoll(
      run,
      token,
      nextPollDelay(run.pollingTime, run.timeout, elapsed),
    )
  }

  const start = (run: PollRun<T>) => {
    stop()
    const token = tokenRef.current
    startedAtRef.current = Date.now()
    armCutOff(run, token, run.timeout)
    schedulePoll(run, token, firstLookMs)
  }

  useEffect(
    () => () => {
      if (stopOnUnmountRef.current) {
        stop()
      }
    },
    // Unmount only. `stop` closes over refs alone, so a stale copy is the
    // same function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return { start, stop }
}

export default usePollChain
